import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Magnetic Field Visualizer — uses the magnetometer (via Magnetometer or
// DeviceOrientationEvent with absolute heading). Earth's natural magnetic
// field is ~25-65 μT depending on latitude; nearby ferrous objects or
// active electronics produce strong deviations.
//
// API selection:
//   1. Try `Magnetometer` (Generic Sensor API) — Chrome Android only.
//   2. Fall back to `deviceorientationabsolute` (gives heading, NOT
//      magnitude — limited but at least responds to magnetic fields).
//
// Web magnetometer access is patchy; if neither works, show unsupported
// with a graceful message.

export default function MagneticFieldVisualizer() {
  const [state, setState] = useState("idle");
  const [reading, setReading] = useState({ x: 0, y: 0, z: 0, magnitude: 0 });
  const [baseline, setBaseline] = useState(null);
  const sensorRef = useRef(null);
  const handlerRef = useRef(null);

  const enable = async () => {
    setState("requesting");

    // Try Generic Sensor API first
    if ("Magnetometer" in window) {
      try {
        // Some browsers need permissions
        if (navigator.permissions) {
          try { await navigator.permissions.query({ name: "magnetometer" }); } catch {}
        }
        const sensor = new window.Magnetometer({ frequency: 30 });
        sensor.addEventListener("reading", () => {
          const x = sensor.x || 0, y = sensor.y || 0, z = sensor.z || 0;
          const magnitude = Math.sqrt(x * x + y * y + z * z);
          setReading({ x, y, z, magnitude });
        });
        sensor.addEventListener("error", () => setState("unsupported"));
        sensor.start();
        sensorRef.current = sensor;
        setState("ok");
        return;
      } catch {
        // Fall through to fallback
      }
    }

    // Fallback: deviceorientationabsolute (direction-only)
    if (typeof DeviceOrientationEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      handlerRef.current = (e) => {
        // No magnitude available — synthesize a "direction only" reading
        const alpha = e.alpha || 0;
        const rad = (alpha * Math.PI) / 180;
        setReading({ x: Math.sin(rad), y: Math.cos(rad), z: 0, magnitude: NaN });
      };
      window.addEventListener("deviceorientationabsolute", handlerRef.current, true);
      // Some browsers don't fire deviceorientationabsolute even when supported
      setTimeout(() => {
        if (handlerRef.current) {
          // Also try plain deviceorientation as a last resort
          window.addEventListener("deviceorientation", handlerRef.current, true);
        }
      }, 500);
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  useEffect(() => () => {
    if (sensorRef.current && sensorRef.current.stop) sensorRef.current.stop();
    if (handlerRef.current) {
      window.removeEventListener("deviceorientationabsolute", handlerRef.current, true);
      window.removeEventListener("deviceorientation", handlerRef.current, true);
    }
  }, []);

  const angle = Math.atan2(reading.x, reading.y) * 180 / Math.PI;
  const hasMagnitude = !isNaN(reading.magnitude);
  const delta = baseline != null && hasMagnitude ? reading.magnitude - baseline : 0;

  // Magnitude bar: Earth's field is ~50 μT typical. Cap at 200 μT for
  // bar display (anything stronger is local interference).
  const barPct = hasMagnitude ? Math.min(100, (reading.magnitude / 200) * 100) : 0;

  return (
    <ToolsShell title="Magnetic Field">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Visualize the magnetic field around you. Move the phone near speakers, motors, or metal objects to see the field change.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state === "requesting" ? "prompt" : state} what="Magnetometer" />
        </div>
      )}

      {state === "ok" && (
        <>
          {/* Direction arrow */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
            <svg width="240" height="240" viewBox="0 0 240 240">
              <circle cx="120" cy="120" r="100" fill="none" stroke="#262839" strokeWidth="1" />
              <circle cx="120" cy="120" r="60" fill="none" stroke="#1F2030" strokeWidth="1" />
              <g transform={`rotate(${angle} 120 120)`}>
                <line x1="120" y1="120" x2="120" y2="35" stroke={TOOLS_ACCENT} strokeWidth="3" strokeLinecap="round" />
                <path d="M120 28 l -10 14 l 20 0 z" fill={TOOLS_ACCENT} />
              </g>
              <circle cx="120" cy="120" r="6" fill={TOOLS_ACCENT} />
            </svg>
          </div>
          <div style={{ textAlign: "center", fontSize: 11, color: "#5B5E7A", textTransform: "uppercase", letterSpacing: 0.6, marginTop: -6 }}>
            {hasMagnitude ? "Field direction" : "Magnetic north (compass)"}
          </div>

          {/* Magnitude bar */}
          {hasMagnitude ? (
            <>
              <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 6, marginTop: 14 }}>Field strength</div>
              <div style={{ width: "100%", height: 18, background: "#1C1E2E", borderRadius: 9, overflow: "hidden" }}>
                <div style={{ width: `${barPct}%`, height: "100%", background: TOOLS_ACCENT, transition: "width 0.1s linear" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#5B5E7A", marginTop: 4 }}>
                <span>0</span><span>50 μT (Earth)</span><span>200+ μT</span>
              </div>

              <div style={{ marginTop: 16, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Total magnitude</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
                      {reading.magnitude.toFixed(1)} μT
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>From baseline</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: delta > 0 ? "#F8B84E" : "#9A9DBE", fontVariantNumeric: "tabular-nums" }}>
                      {baseline == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  onClick={() => setBaseline(reading.magnitude)}
                  style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  Set baseline here
                </button>
                {baseline != null && (
                  <button
                    onClick={() => setBaseline(null)}
                    style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}
                  >
                    Clear baseline
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ marginTop: 14, padding: "14px 16px", background: "#13141F", border: "1px solid #262839", borderRadius: 10, fontSize: 13, color: "#C8CADF", lineHeight: 1.55 }}>
              <strong style={{ color: "#F8B84E" }}>Compass mode (iOS limitation).</strong> Your browser only exposes magnetic <em>direction</em>, not <em>strength</em>. The arrow above is pointing toward magnetic north.
              <div style={{ marginTop: 10, fontSize: 12.5, color: "#9A9DBE" }}>
                <strong style={{ color: "#E8E9F3" }}>To detect a magnet:</strong> hold the phone flat and stable, watch the arrow direction. Bring a magnet very close (within ~5 cm) to one edge of the phone — strong magnets will deflect the arrow noticeably. Weak magnets or distance &gt; 10 cm won't overcome Earth's field (~50 μT).
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: "#5B5E7A" }}>
                For magnitude readings, Chrome on Android usually works. iOS Safari and iOS Chrome don't expose the magnetometer's raw values.
              </div>
            </div>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Earth's field is 25-65 μT depending on latitude. Speakers, motors, transformers, and large iron objects deflect the reading significantly.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
